/**
 * Email service - Calls Supabase Edge Function to send emails securely with organization branding
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/send-email`;

/**
 * Send email to office admin with PDF attachment and download links for documents
 * @param {Object} organization - Organization data (name, logo, email settings, etc.)
 * @param {Object} formData - Form data
 * @param {Blob} pdfBlob - PDF blob
 * @param {Array} uploadedFiles - Array of uploaded files
 * @param {string} confirmationNumber - Confirmation number
 * @param {string} submissionDateTime - Submission date/time
 * @param {Array} filePaths - Array of file paths in storage
 */
export const sendAdminEmail = async (organization, formData, pdfBlob, uploadedFiles, confirmationNumber, submissionDateTime, filePaths = []) => {
  try {
    console.log('Sending admin email via Edge Function...');
    console.log(`Organization: ${organization.name}`);
    
    // ENTERPRISE-LEVEL VALIDATION: Filter out any non-user files (e.g., organization logos)
    const actualUserUploadedFiles = (uploadedFiles || []).filter(file => {
      // Validate this is a legitimate user-uploaded file
      const hasValidName = file && file.name && typeof file.name === 'string';
      const hasValidSize = file && typeof file.size === 'number' && file.size > 0;
      const hasValidType = file && file.type && typeof file.type === 'string';
      
      // Additional check: Exclude files with organization logo names or URLs in their names
      const orgSlug = organization?.slug?.toLowerCase() || '';
      const isNotLogoFile = file.name &&
        !file.name.toLowerCase().includes('logo') &&
        !(orgSlug && file.name.toLowerCase().includes(orgSlug)) &&
        !file.name.toLowerCase().startsWith('http');
      
      return hasValidName && hasValidSize && hasValidType && isNotLogoFile;
    });
    
    console.log(`Including ${actualUserUploadedFiles.length} document download links`);
    
    // Convert PDF blob to base64 (PDF is small enough to attach)
    const pdfBase64 = await blobToBase64(pdfBlob);
    
    // Prepare file information for download links (no attachments)
    const fileInfo = [];
    if (actualUserUploadedFiles.length > 0) {
      actualUserUploadedFiles.forEach((file, index) => {
        fileInfo.push({
          name: file.name,
          size: file.size,
          type: file.type,
          path: filePaths[index] || '', // Storage path for generating signed URL
        });
        console.log(`File info: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      });
    }
    
    // Calculate total file size (only for actual user files)
    const totalSize = actualUserUploadedFiles.reduce((sum, file) => sum + file.size, 0);
    const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
    if (actualUserUploadedFiles.length > 0) {
      console.log(`Total files size: ${totalSizeMB} MB`);
    }
    
    // Call Supabase Edge Function with organization data
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        type: 'admin',
        organization: {
          name: organization.name,
          logo_url: organization.logo_url,
          company_email: organization.company_email,
          company_phone: organization.company_phone,
          email_from_name: organization.email_from_name || organization.name,
          email_from_address: organization.email_from_address || organization.company_email,
          email_reply_to: organization.email_reply_to || organization.company_email,
        },
        formData: {
          taxpayerFirstName: formData.taxpayerFirstName,
          taxpayerLastName: formData.taxpayerLastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          filingStatus: formData.filingStatus,
        },
        pdfBase64: pdfBase64,
        fileInfo: fileInfo, // Send file info instead of base64 attachments
        confirmationNumber: confirmationNumber,
        submissionDateTime: submissionDateTime,
      }),
    });
    
    const result = await response.json();
    
    if (result.success) {
      console.log('Admin email sent successfully:', result.messageId);
      console.log(`PDF attached, ${fileInfo.length} download links included`);
      return { success: true, messageId: result.messageId };
    } else {
      console.error('Failed to send admin email:', result.error);
      return { success: false, error: result.error };
    }
    
  } catch (error) {
    console.error('Error sending admin email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send thank you email to client
 * @param {Object} organization - Organization data (name, logo, email settings, etc.)
 * @param {Object} formData - Form data
 * @param {string} confirmationNumber - Confirmation number
 * @param {string} submissionDateTime - Submission date/time
 */
export const sendClientEmail = async (organization, formData, confirmationNumber, submissionDateTime) => {
  try {
    console.log('Sending client email via Edge Function...');
    console.log(`Organization: ${organization.name}`);
    
    // Call Supabase Edge Function with organization data
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        type: 'client',
        organization: {
          name: organization.name,
          logo_url: organization.logo_url,
          company_email: organization.company_email,
          company_phone: organization.company_phone,
          email_from_name: organization.email_from_name || organization.name,
          email_from_address: organization.email_from_address || organization.company_email,
          email_reply_to: organization.email_reply_to || organization.company_email,
        },
        formData: {
          taxpayerFirstName: formData.taxpayerFirstName,
          taxpayerLastName: formData.taxpayerLastName,
          email: formData.email,
        },
        confirmationNumber: confirmationNumber,
        submissionDateTime: submissionDateTime,
      }),
    });
    
    const result = await response.json();
    
    if (result.success) {
      console.log('Client email sent successfully:', result.messageId);
      return { success: true, messageId: result.messageId };
    } else {
      console.error('Failed to send client email:', result.error);
      return { success: false, error: result.error };
    }
    
  } catch (error) {
    console.error('Error sending client email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Helper function to convert Blob to Base64
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};